'use client';

import { usePathname } from 'next/navigation';
import { usePlausible } from 'next-plausible';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useState } from 'react';
import { useCookie } from 'react-use';
import { v4 as uuidV4 } from 'uuid';

import { useCustomerContext } from '@/context/CustomerContext';
import { segment } from '@/lib/tracking/segmentAnalytics';

import { type FormData, FormStatus } from '../types';

import { getHasSubscribedCookieName } from './getHasSubscribedCookieName';
import { submitForm } from './submitForm';

const STATUS_RESET_TIMEOUT = 3000;

export function useFormSubmit(title: string, formId: number) {
    const [submissionError, setSubmissionError] = useState<string | null>(null);
    const [status, setStatus] = useState<FormStatus | null>(FormStatus.INITIAL);
    const plausible = usePlausible();
    const posthog = usePostHog();
    const pathname = usePathname();
    const { isCustomer } = useCustomerContext();
    const [, setHasSubscribedCookie] = useCookie(getHasSubscribedCookieName(formId));

    const handleSubmit = useCallback(
        async (values: FormData) => {
            setSubmissionError(null);
            setStatus(FormStatus.SUBMITTING);

            const anonymousId = segment.instance?.user().anonymousId();
            const userId = segment.instance?.user().id() || uuidV4();

            try {
                const response = await submitForm(
                    values,
                    pathname ?? '/',
                    userId,
                    anonymousId,
                    isCustomer,
                );

                if (!response.id) {
                    segment.track(`Form Validation Failed`, {
                        ...values,
                        title,
                        ctaId: values.ctaId,
                        form: values.ctaId,
                        path: pathname ?? '/',
                    });

                    plausible('Form Validation Failed', {
                        props: {
                            title,
                            ctaId: values.ctaId,
                            name: values.ctaId,
                            path: pathname ?? '/',
                        },
                    });

                    posthog.capture('Form Validation Failed', {
                        title,
                        ctaId: values.ctaId,
                        email: values.email,
                        form: values.ctaId,
                        path: pathname ?? '/',
                    });

                    throw new Error('Something went wrong processing your input');
                }

                setHasSubscribedCookie('true');
                setStatus(FormStatus.SUBMITTED);

                segment.identify(userId, {
                    email: values.email,
                    ctaId: values.ctaId,
                });

                segment.track('Email Subscribe', {
                    ...values,
                    isCustomer,
                    title,
                    userId,
                    ctaId: values.ctaId,
                    path: pathname ?? '/',
                });

                plausible('Email Subscribe', {
                    props: {
                        isCustomer,
                        title,
                        ctaId: values.ctaId,
                        path: pathname ?? '/',
                    },
                });

                posthog.identify(values.email);
                posthog.capture('Email Subscribe', {
                    title,
                    ctaId: values.ctaId,
                    email: values.email,
                    path: pathname ?? '/',
                });
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);

                segment.track(`Email Subscribe Failed`, {
                    ...values,
                    title,
                    ctaId: values.ctaId,
                    form: values.ctaId,
                    path: pathname ?? '/',
                });

                plausible('Email Subscribe Failed', {
                    props: {
                        title,
                        ctaId: values.ctaId,
                        name: values.ctaId,
                        path: pathname ?? '/',
                    },
                });

                // Should this be captured using `captureException`?
                posthog.capture('Email Subscribe Failed', {
                    title,
                    ctaId: values.ctaId,
                    email: values.email,
                    form: values.ctaId,
                    path: pathname ?? '/',
                });

                setSubmissionError(errorMessage);
                setStatus(FormStatus.ERROR);

                setTimeout(() => {
                    setStatus(FormStatus.INITIAL);
                }, STATUS_RESET_TIMEOUT);
            }
        },
        [pathname, isCustomer, setHasSubscribedCookie, title, plausible, posthog],
    );

    return {
        error: submissionError,
        status,
        handleSubmit,
    };
}
